import '../../../components/Contents/blogspage/blog.css';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useEffect, useMemo, useState } from 'react';
import { Tab, Box, Container } from '@mui/material';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { NewsTabContent } from '../../../components/Contents/blogspage/NewsTabContent';
import { ArticleCardList } from '../article/ArticleCardList';
import { BlogCardList } from '../blog/BlogCardList';
import { EventCardList } from '../event/EventCardList';
import { PodcastCardList } from '../podcast/PodcastCardList';
import { UsecaseCardList } from '../usecase/UsecaseCardList';
import { DetailPageHelmet } from '../helper/DetailPageHelmet';
import { HUB_SECTION_META } from '../../../components/seo/staticPageSeoMeta';
import { getPublicSiteUrl } from '../siteUrl';

const tabMenuStyle = {
  textTransform: 'none',
  padding: '1rem',
};

const SECTION_TO_VALUE = {
  all: '1',
  events: '2',
  news: '4',
  podcasts: '5',
  blogs: '3',
  usecases: '6',
};

const VALUE_TO_PATH = {
  '1': '/articles/all',
  '2': '/articles/events',
  '4': '/articles/news',
  '5': '/articles/podcasts',
  '3': '/articles/blogs',
  '6': '/articles/usecases',
};

const VALID = new Set(['all', 'events', 'podcasts', 'blogs', 'news', 'usecases']);

export function HubSection() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [value, setValue] = useState(SECTION_TO_VALUE[section] || '1');

  useEffect(() => {
    setValue(SECTION_TO_VALUE[section] || '1');
  }, [section]);

  const sectionMeta = VALID.has(section) ? HUB_SECTION_META[section] : null;
  const siteUrl = getPublicSiteUrl();
  const canonicalUrl = useMemo(() => {
    if (!siteUrl || !sectionMeta?.canonicalPath) return '';
    return `${siteUrl}${sectionMeta.canonicalPath}`;
  }, [siteUrl, sectionMeta]);

  if (!VALID.has(section)) {
    return <Navigate to="/articles/all" replace />;
  }

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const path = VALUE_TO_PATH[newValue];
    if (path) navigate(path);
  };

  return (
    <Box className="w-full">
      <DetailPageHelmet
        pageTitle={sectionMeta.pageTitle}
        metaDescription={sectionMeta.metaDescription}
        canonicalUrl={canonicalUrl}
      />
      <Container maxWidth="lg">
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList
              sx={{ paddingTop: '1rem', overflowX: 'auto' }}
              onChange={handleChange}
              aria-label="New hub content tabs"
            >
              <Tab label="All" value="1" sx={tabMenuStyle} />
              <Tab label="Events" value="2" sx={tabMenuStyle} />
              <Tab label="News" value="4" sx={tabMenuStyle} />
              <Tab label="Podcasts" value="5" sx={tabMenuStyle} />
              <Tab label="Blogs" value="3" sx={tabMenuStyle} />
              <Tab label="Usecases" value="6" sx={tabMenuStyle} />
            </TabList>
          </Box>
          <TabPanel value="1" sx={{ px: 0 }}>
            {section === 'all' && <ArticleCardList />}
          </TabPanel>
          <TabPanel value="2" sx={{ px: 0 }}>
            {section === 'events' && <EventCardList />}
          </TabPanel>
          <TabPanel value="4" sx={{ px: 0 }}>
            {section === 'news' && <NewsTabContent />}
          </TabPanel>
          <TabPanel value="5" sx={{ px: 0 }}>
            {section === 'podcasts' && <PodcastCardList />}
          </TabPanel>
          <TabPanel value="3" sx={{ px: 0 }}>
            {section === 'blogs' && <BlogCardList />}
          </TabPanel>
          <TabPanel value="6" sx={{ px: 0 }}>
            {section === 'usecases' && <UsecaseCardList />}
          </TabPanel>
        </TabContext>
      </Container>
    </Box>
  );
}
